import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import ManagerSubscriptionsApiClient from '../../../../api-client/ManagerSubscriptionsApiClient';
import { store } from '../../../../store/store';
import { conMotivo } from '../../../components/Util/erroreServer';
import { usePageTitle, useStatoInSolaLettura } from '../../../layouts/PageHeaderExtra';

/*
 * Scheda di una transazione dell'abbonamento al gestionale (03/10/2026): «Download XML» e «Download PDF»
 * della fattura erano nel menu a fine riga dello «Storico Transazioni e Fatture», ora stanno qui.
 * Non c'è un'API per leggere una transazione per id: si rilegge lo storico e la si cerca per id
 * (intanto si mostra la riga arrivata dalla lista).
 */

const LIST_URL = '/struttura/strumenti/abbonamento-info';

const STATI_ABBONAMENTO = {
    active: { label: 'Attivo', tono: 'attivo' },
    expired: { label: 'Scaduto', tono: 'non-attivo' },
    free: { label: 'Gratuito', tono: 'attivo' },
    inactive: { label: 'Inattivo', tono: 'non-attivo' },
    refunded: { label: 'Rimborsato', tono: 'bozza' },
};

const STATI_FATTURA = {
    SENT: 'Inviata',
    ACCEPTED: 'Accettata',
    DELIVERED: 'Consegnata',
    REJECTED: 'Scartata SDI',
    NOT_SENT: 'Non inviata',
    NOT_GENERATED: 'Non generata',
};

const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString('it-IT') : '—');

export const SchedaTransazione = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [transazione, setTransazione] = useState(location.state?.transazione || null);
    const [caricata, setCaricata] = useState(false);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        const carica = async () => {
            try {
                const token = store.getState().auth.auth.idToken;
                const res = await new ManagerSubscriptionsApiClient().getTransactions(token);
                if (res && !res.error && Array.isArray(res)) {
                    const trovata = res.find((t) => String(t.id) === String(id));
                    if (trovata) setTransazione(trovata);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setCaricata(true);
            }
        };
        carica();
    }, [id]);

    const data = transazione ? (transazione.createdAt || transazione.startDate) : null;
    usePageTitle(transazione ? (transazione.transactionId ? `Transazione ${transazione.transactionId}` : `Abbonamento del ${formatDate(data)}`) : '');
    const stato = transazione ? (STATI_ABBONAMENTO[transazione.status] || { label: transazione.status, tono: 'bozza' }) : { label: '', tono: 'bozza' };
    useStatoInSolaLettura(stato.label, stato.tono);

    const indietro = () => navigate(LIST_URL);

    if (!transazione) {
        return (
            <>
                <Card>
                    <Card.Body className="sid-card-body--spaced">
                        {caricata ? 'Transazione non trovata.' : 'Caricamento in corso...'}
                    </Card.Body>
                </Card>
                <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                    <span />
                    <button type="button" className="btn btn-dark light" onClick={indietro}>Indietro</button>
                </div>
            </>
        );
    }

    const conFattura = transazione.invoiceStatus && transazione.invoiceStatus !== 'NOT_GENERATED';

    const scaricaPdf = async () => {
        setDownloading('pdf');
        try {
            const token = store.getState().auth.auth.idToken;
            const res = await new ManagerSubscriptionsApiClient().downloadInvoicePdf(token, transazione.id);
            if (res && res.pdfUrl) {
                const link = document.createElement('a');
                link.href = res.pdfUrl;
                link.download = `Fattura_${transazione.transactionId || transazione.id}.pdf`;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
            } else {
                alert(conMotivo('Si è verificato un errore durante il recupero del PDF.'));
            }
        } catch (error) {
            console.error('Errore download fattura', error);
        } finally {
            setDownloading(null);
        }
    };

    const scaricaXml = async () => {
        setDownloading('xml');
        try {
            const token = store.getState().auth.auth.idToken;
            const xmlBlob = await new ManagerSubscriptionsApiClient().downloadInvoiceXml(token, transazione.id);
            const url = window.URL.createObjectURL(new Blob([xmlBlob], { type: 'application/xml' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Fattura_${transazione.transactionId || transazione.id}.xml`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Errore download fattura xml', error);
        } finally {
            setDownloading(null);
        }
    };

    return (
        <>
            <Card>
                <Card.Body className="sid-card-body--spaced">
                    <h5 className="sid-form-section">Transazione</h5>
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Label>Data</Form.Label>
                            <div className="sid-readonly-value">{formatDate(data)}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Transazione</Form.Label>
                            <div className="sid-readonly-value">{transazione.transactionId || '—'}</div>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Importo</Form.Label>
                            <div className="sid-readonly-value">{transazione.priceEur ? `€ ${Number(transazione.priceEur).toFixed(2)}` : 'Gratuito'}</div>
                        </Col>
                        {(transazione.startDate || transazione.endDate) && (
                            <Col md={6}>
                                <Form.Label>Periodo</Form.Label>
                                <div className="sid-readonly-value">{formatDate(transazione.startDate)} – {formatDate(transazione.endDate)}</div>
                            </Col>
                        )}
                    </Row>

                    <h5 className="sid-form-section mt-4">Fattura</h5>
                    <Row className="g-4">
                        <Col md={6}>
                            <Form.Label>Stato fattura</Form.Label>
                            <div className="sid-readonly-value">
                                {transazione.invoiceStatus ? (STATI_FATTURA[transazione.invoiceStatus] || transazione.invoiceStatus) : 'Senza fattura'}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between align-items-center gap-4 mt-4">
                <span />
                <div className="d-flex gap-4">
                    <button type="button" className="btn btn-dark light" onClick={indietro}>Indietro</button>
                    {conFattura && (
                        <button type="button" className="btn btn-dark light" onClick={scaricaXml} disabled={downloading === 'xml'}>
                            {downloading === 'xml' ? 'Scaricamento...' : 'Scarica XML'}
                        </button>
                    )}
                    {conFattura && (
                        <Button className="btn btn-primary" onClick={scaricaPdf} disabled={downloading === 'pdf'}>
                            {downloading === 'pdf' ? 'Scaricamento...' : 'Scarica PDF'}
                        </Button>
                    )}
                </div>
            </div>
        </>
    );
};

export default SchedaTransazione;
